import type { ColumnSection } from "building-types/columns/columnTypes";
import { hardCodedAutoPlacedPropLabels } from "building-types/entities/labelTypes";
import type { ColonnadePoint } from "building-types/porches/porchColonnadePoint";
import { ColonnadePointType } from "building-types/porches/porchColonnadePoint";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { PorchKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { ExteriorMaterialOverrideWithKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import {
  ColumnDimensionType,
  PorchColumnBracketType,
  PropCatalogType,
} from "building-types/settings/settingsTypesEnum";
import type { ColumnViewNoteTable } from "building-types/settings/viewNotesSettings";
import { ColumnViewNoteType, PorchViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { getPropSrc } from "doc/elements/propCatalog/propCatalogHelpers";
import type { MergedPropLibrary } from "doc/propTools";
import { propNew } from "doc/propTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { extrusionNew, faceByFourPoints, faceNew } from "geom/brep";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { extrudeRectWithRotation } from "geom/extrude";
import type GeomTagGroup from "geom/geomTagGroup";
import { polyLine2MiterPt } from "geom/offset";
import { planeNewXY, planeNewXYNeg } from "geom/plane";
import { polyLine2Edge, polyLine2Reverse, polyLine2Rotate, polyLine2ScaleY, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2Dot, vec2NormsCollinear, vec2Perp, vec2Rotate } from "geom/vec2";
import { uvGenerationForColumnSection, uvTransformationForColumnSection } from "../../utils/uvGenerationForEntities";
import { porchColumnBraceNew } from "./porchColumnBrace";
import {
  BRACE_DIST,
  BRACE_WIDTH,
  exteriorMaterialOverrideWithKeyMeasuresNew,
  getColumnFromSettings,
  resolvePorchPlanSettings,
  type PorchColumnSettings,
} from "./porchUtils";

export class ColumnBuilder {
  constructor(
    private readonly bottomDatum: number,
    private readonly topDatum: number,
    private readonly porchSettingsColumn: PorchColumnSettings,
    public readonly colonnadePoints: ColonnadePoint[],
    private readonly columnProfiles: PolyLine2[],
    private readonly group: GeomTagGroup<ExteriorMaterialOverrideWithKeyMeasures>,
    private readonly propCatalog: PropAttributeTable,
    private readonly mergedPropLibrary: MergedPropLibrary,
    private readonly props: Prop[],
    private readonly room: Room,
    private readonly keyMeasures: PorchKeyMeasures,
    private readonly columnCasingOffset: number,
    private readonly porchSettings: PorchSettingsBase,
    private readonly planSettings: PlanSettings,
  ) {
    this.topDatum = topDatum;
    this.keyMeasures.columnHeight = topDatum - bottomDatum;
  }

  async makeSingleOrMultiColumn(
    isCorner: boolean,
    sections: ColumnSection[],
    columnStructureX: number,
    columnStructureY: number,
    isStartOrEndOfColonnadeEdge: boolean,
    edgeTangent: Vec2,
    previousEdgeTangent: Vec2,
    braceDirs: Vec2[] | undefined,
    multiColumn: boolean,
    columnViewNotes: ColumnViewNoteTable | undefined,
    rotated: boolean,
    previousEdgeAdd = false,
    halfEdges: (number | undefined)[],
  ) {
    const {
      multiColumnSpread,
      internalColumnStructureSize,
      multiColumn: multiColumnSetting,
    } = this.porchSettingsColumn;

    assertIsNumber(multiColumnSpread);

    const [edgeTangentX, edgeTangentY] = edgeTangent;
    const [previousEdgeTangentX, previousEdgeTangentY] = previousEdgeTangent;

    const columnDimensionType = this.planSettings.columnDimensionType;
    const resolvedPorchPlanSettings = resolvePorchPlanSettings(this.planSettings, this.porchSettings);

    if (previousEdgeAdd) {
      //IS CORNER
      assertIsNotNullOrUndefined(braceDirs);

      const [nx, ny] = braceDirs[0];
      const [cx1, cy1] = [columnStructureX + nx * multiColumnSpread, columnStructureY + ny * multiColumnSpread];
      const [cornerCasingX, cornerCasingY] =
        Math.abs(this.columnCasingOffset) < DIST_EPSILON
          ? [cx1, cy1]
          : polyLine2MiterPt(
              columnStructureX + previousEdgeTangentX,
              columnStructureY + previousEdgeTangentY,
              columnStructureX,
              columnStructureY,
              columnStructureX + edgeTangentX,
              columnStructureY + edgeTangentY,
              this.columnCasingOffset,
            );
      this.makeColumnFromColumnSections(
        cornerCasingX,
        cornerCasingY,
        cx1,
        cy1,
        sections,
        edgeTangent,
        columnViewNotes,
        rotated,
      );

      this.colonnadePoints.push({
        type: ColonnadePointType.MULTI_COLUMN,
        x: cx1,
        y: cy1,
        width: internalColumnStructureSize,
        halfEdges,
      });

      if (columnDimensionType !== ColumnDimensionType.NONE) {
        // Because multi-columns generated on a corner can be placed on both the current and prior edge,
        // it's necessary to determine which edge tangent matches the edge on which the column is placed -
        // and sometimes that will be the previous one.
        const columnOnCurrentSegment = vec2NormsCollinear(edgeTangentX, edgeTangentY, nx, ny);
        const columnDim = this.dimensionForColumn(
          cx1,
          cy1,
          columnOnCurrentSegment ? edgeTangentX : -previousEdgeTangentX,
          columnOnCurrentSegment ? edgeTangentY : -previousEdgeTangentY,
          previousEdgeTangentX,
          previousEdgeTangentY,
          false,
          columnDimensionType,
          this.porchSettingsColumn.internalColumnStructureSize,
          resolvedPorchPlanSettings.includePorchColumnStructure,
        );

        assertIsNotNullOrUndefined(columnDim);
        const [dimX, dimY] = columnDim;

        this.colonnadePoints.push({
          type: ColonnadePointType.MULTI_COLUMN_DIM,
          x: dimX,
          y: dimY,
          halfEdges,
        });
      }
    }

    if (multiColumn) {
      let cornerColumnPlaced = false;
      for (const [bx, by] of braceDirs || []) {
        const [nx, ny] =
          vec2Dot(bx, by, edgeTangentX, edgeTangentY) > ONE_MINUS_DIST_EPSILON ? vec2Perp(bx, by) : vec2Perp(-bx, -by);
        if (!isStartOrEndOfColonnadeEdge) {
          //IS NOT CORNER
          const [cx1, cy1] = [
            columnStructureX - (bx * multiColumnSpread) / 2,
            columnStructureY - (by * multiColumnSpread) / 2,
          ];
          const [x, y] =
            Math.abs(this.columnCasingOffset) < DIST_EPSILON
              ? [cx1, cy1]
              : [cx1 + nx * this.columnCasingOffset, cy1 + ny * this.columnCasingOffset];
          this.makeColumnFromColumnSections(x, y, cx1, cy1, sections, edgeTangent, columnViewNotes, rotated);
          this.colonnadePoints.push({
            type: ColonnadePointType.MULTI_COLUMN,
            x: cx1,
            y: cy1,
            width: internalColumnStructureSize,
            halfEdges,
          });

          if (columnDimensionType !== ColumnDimensionType.NONE) {
            const columnDim = this.dimensionForColumn(
              cx1,
              cy1,
              edgeTangentX,
              edgeTangentY,
              previousEdgeTangentX,
              previousEdgeTangentY,
              false,
              columnDimensionType,
              this.porchSettingsColumn.internalColumnStructureSize,
              resolvedPorchPlanSettings.includePorchColumnStructure,
            );
            assertIsNotNullOrUndefined(columnDim);
            const [dimX, dimY] = columnDim;
            this.colonnadePoints.push({
              type: ColonnadePointType.MULTI_COLUMN_DIM,
              x: dimX,
              y: dimY,
              halfEdges,
            });
          }
        } else {
          const [cx1, cy1] = isCorner
            ? [
                columnStructureX + bx * (multiColumnSpread - this.columnCasingOffset),
                columnStructureY + by * (multiColumnSpread - this.columnCasingOffset),
              ]
            : [columnStructureX + bx * multiColumnSpread, columnStructureY + by * multiColumnSpread];

          const [x, y] =
            Math.abs(this.columnCasingOffset) < DIST_EPSILON
              ? [cx1, cy1]
              : [cx1 + nx * this.columnCasingOffset, cy1 + ny * this.columnCasingOffset];
          this.makeColumnFromColumnSections(x, y, cx1, cy1, sections, edgeTangent, columnViewNotes, rotated);

          this.colonnadePoints.push({
            type: ColonnadePointType.MULTI_COLUMN,
            x: cx1,
            y: cy1,
            width: internalColumnStructureSize,
            halfEdges,
          });

          if (columnDimensionType !== ColumnDimensionType.NONE) {
            // Because multi-columns generated on a corner can be placed on both the current and prior edge,
            // it's necessary to determine which edge tangent matches the edge on which the column is placed -
            // and sometimes that will be the previous one.
            const columnOnCurrentSegment = vec2NormsCollinear(edgeTangentX, edgeTangentY, bx, by);
            const columnDim = this.dimensionForColumn(
              cx1,
              cy1,
              columnOnCurrentSegment ? edgeTangentX : -previousEdgeTangentX,
              columnOnCurrentSegment ? edgeTangentY : -previousEdgeTangentY,
              previousEdgeTangentX,
              previousEdgeTangentY,
              false,
              columnDimensionType,
              this.porchSettingsColumn.internalColumnStructureSize,
              resolvedPorchPlanSettings.includePorchColumnStructure,
            );
            assertIsNotNullOrUndefined(columnDim);
            const [dimX, dimY] = columnDim;
            this.colonnadePoints.push({
              type: ColonnadePointType.MULTI_COLUMN_DIM,
              x: dimX,
              y: dimY,
              halfEdges,
            });
          }

          if (!cornerColumnPlaced) {
            //IS CORNER
            cornerColumnPlaced = true;
            const [cornerCasingX, cornerCasingY] =
              Math.abs(this.columnCasingOffset) < DIST_EPSILON
                ? [columnStructureX, columnStructureY]
                : polyLine2MiterPt(
                    columnStructureX + previousEdgeTangentX,
                    columnStructureY + previousEdgeTangentY,
                    columnStructureX,
                    columnStructureY,
                    columnStructureX + edgeTangentX,
                    columnStructureY + edgeTangentY,
                    this.columnCasingOffset,
                  );
            this.makeColumnFromColumnSections(
              cornerCasingX,
              cornerCasingY,
              columnStructureX,
              columnStructureY,
              sections,
              edgeTangent,
              columnViewNotes,
              rotated,
            );
            this.colonnadePoints.push({
              type: ColonnadePointType.MULTI_COLUMN,
              x: columnStructureX,
              y: columnStructureY,
              width: internalColumnStructureSize,
              halfEdges,
            });

            if (columnDimensionType !== ColumnDimensionType.NONE) {
              const columnDim2 = this.dimensionForColumn(
                columnStructureX,
                columnStructureY,
                edgeTangentX,
                edgeTangentY,
                previousEdgeTangentX,
                previousEdgeTangentY,
                true,
                columnDimensionType,
                this.porchSettingsColumn.internalColumnStructureSize,
                resolvedPorchPlanSettings.includePorchColumnStructure,
              );
              assertIsNotNullOrUndefined(columnDim2);
              const [dimX, dimY] = columnDim2;
              this.colonnadePoints.push({
                type: ColonnadePointType.MULTI_COLUMN_DIM,
                x: dimX,
                y: dimY,
                halfEdges,
              });
            }
          }
        }
      }
    } else {
      if (!multiColumnSetting) {
        for (const [nx, ny] of braceDirs || []) {
          await this.makeColumnBraceIfNeeded(columnStructureX, columnStructureY, nx, ny, sections, edgeTangent);
        }
      }

      const [x, y] = isCorner
        ? polyLine2MiterPt(
            columnStructureX + previousEdgeTangentX,
            columnStructureY + previousEdgeTangentY,
            columnStructureX,
            columnStructureY,
            columnStructureX + edgeTangentX,
            columnStructureY + edgeTangentY,
            this.columnCasingOffset,
          )
        : polyLine2MiterPt(
            columnStructureX - edgeTangentX,
            columnStructureY - edgeTangentY,
            columnStructureX,
            columnStructureY,
            columnStructureX + edgeTangentX,
            columnStructureY + edgeTangentY,
            this.columnCasingOffset,
          );

      this.makeColumnFromColumnSections(
        x,
        y,
        columnStructureX,
        columnStructureY,
        sections,
        edgeTangent,
        columnViewNotes,
        rotated,
      );

      this.colonnadePoints.push({
        type: ColonnadePointType.COLUMN,
        x: columnStructureX,
        y: columnStructureY,
        width: internalColumnStructureSize,
        halfEdges,
      });

      if (columnDimensionType !== ColumnDimensionType.NONE) {
        const columnDimP = this.dimensionForColumn(
          columnStructureX,
          columnStructureY,
          edgeTangentX,
          edgeTangentY,
          previousEdgeTangentX,
          previousEdgeTangentY,
          isCorner,
          columnDimensionType,
          this.porchSettingsColumn.internalColumnStructureSize,
          resolvedPorchPlanSettings.includePorchColumnStructure,
        );
        assertIsNotNullOrUndefined(columnDimP);
        const [px, py] = columnDimP;
        this.colonnadePoints.push({
          type: ColonnadePointType.COLUMN_DIM,
          x: px,
          y: py,
          halfEdges,
        });
      }
    }
  }

  /**
   * Calculates a dimension point for a column based on the selected ColumnDimensionType.
   */
  private dimensionForColumn(
    columnStructureX: number,
    columnStructureY: number,
    edgeTangentX: number,
    edgeTangentY: number,
    previousEdgeTangentX: number,
    previousEdgeTangentY: number,
    isCorner: boolean,
    columnDimensionType: Exclude<ColumnDimensionType, ColumnDimensionType.NONE>,
    columnStructureSize: number,
    includePorchColumnStructure?: boolean,
  ) {
    const internalColumnStructureSizeHalf = columnStructureSize / 2;

    if (columnDimensionType === ColumnDimensionType.CENTERLINE || !includePorchColumnStructure) {
      // We don't use polyLine2MiterPt for CENTERLINE because the points for the corner column must
      // be center of structure, and all the other points must be colinear with that.
      return [columnStructureX, columnStructureY];
    } else if (columnDimensionType === ColumnDimensionType.HYBRID) {
      return isCorner
        ? polyLine2MiterPt(
            columnStructureX + previousEdgeTangentX,
            columnStructureY + previousEdgeTangentY,
            columnStructureX,
            columnStructureY,
            columnStructureX + edgeTangentX,
            columnStructureY + edgeTangentY,
            internalColumnStructureSizeHalf,
          )
        : polyLine2MiterPt(
            columnStructureX - edgeTangentX,
            columnStructureY - edgeTangentY,
            columnStructureX,
            columnStructureY,
            columnStructureX + edgeTangentX,
            columnStructureY + edgeTangentY,
            internalColumnStructureSizeHalf,
          );
    } else if (columnDimensionType === ColumnDimensionType.EDGE_OF_FRAMING) {
      if (isCorner) {
        return polyLine2MiterPt(
          columnStructureX + previousEdgeTangentX,
          columnStructureY + previousEdgeTangentY,
          columnStructureX,
          columnStructureY,
          columnStructureX + edgeTangentX,
          columnStructureY + edgeTangentY,
          internalColumnStructureSizeHalf,
        );
      } else {
        // Non-corner columns are dimensioned from the bottom left point of column structure.
        const lowerLeftX = columnStructureX - internalColumnStructureSizeHalf;
        const lowerLeftY = columnStructureY - internalColumnStructureSizeHalf;

        // If the bottom left point lies on the inside of the porch, shift to either the bottom right or top left point
        // based on the tangent. This retains the effect of dimensioning to the bottom left point while keeping all dimension
        // points on the segment colinear.
        const edgeNormal = vec2Perp(edgeTangentX, edgeTangentY);
        if (vec2Dot(edgeNormal[0], edgeNormal[1], lowerLeftX - columnStructureX, lowerLeftY - columnStructureY) > 0) {
          return [lowerLeftX, lowerLeftY];
        } else {
          return polyLine2MiterPt(
            lowerLeftX - edgeTangentX,
            lowerLeftY - edgeTangentY,
            lowerLeftX,
            lowerLeftY,
            lowerLeftX + edgeTangentX,
            lowerLeftY + edgeTangentY,
            columnStructureSize,
          );
        }
      }
    }

    return undefined;
  }

  makeColumnFromColumnSections(
    x: number,
    y: number,
    structuralX: number,
    structuralY: number,
    sections: ColumnSection[],
    edgeTangent: Vec2,
    columnViewNotes: ColumnViewNoteTable | undefined,
    rotated: boolean,
  ) {
    const columnIndex: number = this.keyMeasures.columnCount;
    this.keyMeasures.columnCount++;

    if (rotated) {
      // swap depth for width if rotated
      sections = sections.map((s) => ({ ...s, width: s.depth, depth: s.width }));
    }

    const columnHeight = this.topDatum - this.bottomDatum;
    const structuralSize = this.porchSettingsColumn.internalColumnStructureSize;
    const structuralProfile = faceNew(
      [polyLine2SetCW(rect2ByCenterWidthHeight(structuralX, structuralY, structuralSize, structuralSize))],
      planeNewXY(0, 0, this.bottomDatum),
    );
    this.group.add(
      extrusionNew(structuralProfile, 0, 0, columnHeight),
      exteriorMaterialOverrideWithKeyMeasuresNew(
        undefined,
        undefined,
        PorchKeyMeasuresGeometryType.COLUMN_STRUCTURE,
        undefined,
        undefined,
        columnIndex,
      ),
    );
    const zeroHeight = calcZeroHeight(sections, columnHeight);
    let currentHeight = this.bottomDatum;
    let i = 0;

    const firstColumnSectionWidth = sections.length > 0 ? sections[0].width : 0;
    const firstColumnSectionDepth = (sections.length > 0 ? sections[0].depth : 0) ?? firstColumnSectionWidth;
    const [tx, ty] = edgeTangent;
    const rotation = vec2AngleBetween(0, 1, tx, ty);
    this.columnProfiles.push(
      polyLine2Rotate(
        rect2ByCenterWidthHeight(x, y, firstColumnSectionDepth * 2, firstColumnSectionWidth * 2),
        rotation,
        x,
        y,
      ),
    );

    // a new descriptor for each column
    const columnViewEntityDescriptor = columnViewNotes
      ? viewEntityDescriptorNew(uid(), ViewEntityType.COLUMN, columnViewNotes[ColumnViewNoteType.COLUMN])
      : undefined;

    for (const section of sections) {
      const sectionWidth = section.width;
      const sectionDepth = section.depth ?? section.width;

      const materialOverride: ExteriorMaterialOverrideWithKeyMeasures = exteriorMaterialOverrideWithKeyMeasuresNew(
        section.paletteId,
        uvGenerationForColumnSection(section.materialRotation),
        PorchKeyMeasuresGeometryType.COLUMN,
        columnViewEntityDescriptor,
        uvTransformationForColumnSection(section.materialRotation),
        columnIndex,
      );

      if (section.taper) {
        const topSliceWidth = sections[i + 1]?.width ?? sectionWidth;
        const topSliceDepth = sections[i + 1]?.depth ?? sections[i + 1]?.width ?? sectionDepth;
        const height = section.height > 0 ? section.height : zeroHeight;
        const bottomSquare = polyLine2Rotate(
          rect2ByCenterWidthHeight(x, y, sectionDepth * 2, sectionWidth * 2),
          rotation,
          x,
          y,
        );
        const topSquare = polyLine2Rotate(
          rect2ByCenterWidthHeight(x, y, topSliceDepth * 2, topSliceWidth * 2),
          rotation,
          x,
          y,
        );

        for (let j = 0; j < 4; j += 1) {
          const b = polyLine2Edge(bottomSquare, j);
          const t = polyLine2Edge(topSquare, j);

          const face = faceByFourPoints(
            b[0],
            b[1],
            currentHeight,
            b[2],
            b[3],
            currentHeight,
            t[2],
            t[3],
            currentHeight + height,
            t[0],
            t[1],
            currentHeight + height,
          );
          if (!face) {
            continue;
          }
          this.group.add(face, materialOverride);
        }

        // We need to make the geometry solid so that it looks correct when clipped in interior (dollhouse) view, so we add top and bottom caps.
        const topPlane = planeNewXY(0, 0, currentHeight + height);
        polyLine2Reverse(topSquare);
        const topCap = faceNew([topSquare], topPlane);
        this.group.add(topCap, materialOverride);
        const bottomPlane = planeNewXYNeg(0, 0, currentHeight);
        polyLine2ScaleY(bottomSquare, -1);
        const bottomCap = faceNew([bottomSquare], bottomPlane);
        this.group.add(bottomCap, materialOverride);

        currentHeight += height;
        i += 1;
      } else {
        const height = section.height > 0 ? section.height : zeroHeight;
        this.group.add(
          extrudeRectWithRotation(x, y, currentHeight, height, sectionDepth * 2, sectionWidth * 2, rotation),
          materialOverride,
        );
        currentHeight += height;
        i += 1;
      }
    }
  }

  async makeColumnBraceIfNeeded(
    x: number,
    y: number,
    nx: number,
    ny: number,
    slices: ColumnSection[],
    edgeTangent: Vec2,
  ): Promise<void> {
    const columnBracketViewEntityDescriptor = viewEntityDescriptorNew(
      uid(),
      ViewEntityType.COLUMN_BRACKET,
      this.porchSettings.viewNotes?.[PorchViewNoteType.COLUMN_BRACKET],
    );

    if (!this.porchSettings.columnBracketPropId) {
      return;
    }

    if ((this.porchSettings.columnBracketPropId as any) === PorchColumnBracketType.TRIANGLE) {
      this.group.add(
        porchColumnBraceNew(x, y, nx, ny, this.topDatum, BRACE_DIST, BRACE_WIDTH),
        exteriorMaterialOverrideWithKeyMeasuresNew(
          "trimExterior0",
          undefined,
          PorchKeyMeasuresGeometryType.NONE,
          columnBracketViewEntityDescriptor,
        ),
      );
      return;
    } else {
      const propCatalog = this.propCatalog;
      const propId = this.porchSettings.columnBracketPropId;
      const propSrc = await getPropSrc(propCatalog, propId, this.mergedPropLibrary);

      if (!propSrc) {
        return;
      }

      const [braceDirX, braceDirY] = [nx, ny];
      const angleBetween = vec2AngleBetween(0, -1, braceDirX, braceDirY);
      const rotationDegrees = radianToDegree(angleBetween);
      const propX = propSrc.boundingBox.maxX * GLTF_ASSET_SCALE;
      const [propAdjustX, propAdjustY] = vec2Rotate(-propX * 0.5, 0.0, angleBetween);
      const propZ = propSrc.boundingBox.maxZ * GLTF_ASSET_SCALE;
      const propHeight = (propSrc.boundingBox.maxZ - propSrc.boundingBox.minZ) * GLTF_ASSET_SCALE;
      const bottomOfBraceProp = this.topDatum - propZ;
      const columnHeight = this.topDatum - this.bottomDatum;
      const [colWidth, colDepth] = columnSizeAtHeight(
        columnHeight - propHeight,
        slices,
        this.topDatum,
        this.bottomDatum,
      );
      // check for auto column rotation (occurs at param 1)
      const [tx, ty] = edgeTangent;
      const aligned = Math.abs(nx) === Math.abs(tx) && Math.abs(ny) === Math.abs(ty);
      const colOffset = aligned ? colWidth : colDepth;

      const startBracket = propNew({
        room: this.room,
        src: propId,
        x: x + colOffset * braceDirX + propAdjustX,
        y: y + colOffset * braceDirY + propAdjustY,
        z: bottomOfBraceProp,
        rotationDegrees,
        descriptor: columnBracketViewEntityDescriptor,
        catalogType: PropCatalogType.PROP_CATALOG,
        isExterior: true,
        labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
      });
      this.props.push(startBracket);

      return;
    }
  }
}

export function columnBottomWidthFromColumnSections(slices: ColumnSection[]) {
  return slices && slices.length ? slices[0].width : 0;
}

export function columnBottomWidthFromPorchSettings(
  porchSettings: PorchSettingsBase | undefined,
  columnSettings: ColumnCatalogSettings,
) {
  const column = getColumnFromSettings(porchSettings, columnSettings);
  return columnBottomWidthFromColumnSections(column.sections);
}

function columnSizeAtHeight(height: number, slices: ColumnSection[], ceilingHeight: number, startHeight: number) {
  const zeroHeight = calcZeroHeight(slices, ceilingHeight);
  let currentHeight = startHeight;
  for (const slice of slices) {
    const sliceHeight = slice.height > 0 ? slice.height : zeroHeight;
    currentHeight += sliceHeight;
    if (currentHeight >= height) {
      return [slice.width, slice.depth ?? slice.width];
    }
  }
  return [0, 0];
}

function calcZeroHeight(slices: ColumnSection[], ceilingHeight: number) {
  let slicesHeight = 0;
  let zeroSlices = 0;
  for (const slice of slices) {
    slicesHeight += slice.height;
    if (slice.height === 0) {
      zeroSlices++;
    }
  }
  return zeroSlices > 0 ? (ceilingHeight - slicesHeight) / zeroSlices : 0;
}
